<script setup>
import { ref, watch, onMounted, getCurrentInstance } from "vue"
import { useIdle, useEventListener } from '@vueuse/core'
import { createFilterWrapper, throttleFilter, timestamp } from '@vueuse/shared'
import { $bus } from '@/common/events'
import { useScreensaver } from '@/composables/screensaver'
import Bounce from '@/components/Screensaver/Bounce.vue'
import WindowsRetro from '@/components/Screensaver/WindowsRetro.vue'

const instance = getCurrentInstance();
const { idle } = useScreensaver({ instance });

const props = defineProps({
  enabled: {
    type: Boolean,
    default: true
  },
  timeout: {
    type: Number,
    default: 5 * 60 * 1000
  },
  theme: {
    type: String,
    default: "bounce" //blank, bounce, windows-retro
  },
});

const emit = defineEmits(["change"]);

defineExpose({ show, hide });

//Commented: cannot be dynamically changed or turned off
//const { idle, lastActive, reset } = useIdle(5 * 60 * 1000) // 5 min

let timer = null;

//const idle = ref(false);
const lastActive = ref(timestamp());
const defaultEvents = ['mousemove', 'mousedown', 'resize', 'keydown', 'touchstart', 'wheel'];

function reset() {
  idle.value = false;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (props.enabled) {
    let timeout = props.timeout;
    timer = setTimeout(() => {
      idle.value = true;
    }, timeout > 0 ? timeout : 1);
  }
}

function init() {
  let listenForVisibilityChange = true;
  let eventFilter = throttleFilter(50);
  let events = defaultEvents;

  const onEvent = createFilterWrapper(
    eventFilter,
    () => {
      lastActive.value = timestamp()
      reset()
    },
  )

  if (window) {
    const document = window.document
    for (const event of events)
      useEventListener(window, event, onEvent, { passive: true })

    if (listenForVisibilityChange) {
      useEventListener(document, 'visibilitychange', () => {
        if (!document.hidden)
          onEvent()
      })
    }

    reset()
  }
}

function addBodyClass() {
  if (document.body) {
    document.body.classList.add("screensaver");
  }
}

function removeBodyClass() {
  if (document.body) {
    document.body.classList.remove("screensaver");
  }
}

function show() {
  idle.value = true;
}

function hide() {
  idle.value = false;
}

watch(idle, (idleValue) => {
  emit("change", { idle: idleValue });

  if (idleValue) {
    addBodyClass();
  }
  else {
    removeBodyClass();
  }
});

watch(() => props.enabled, (enabled) => {
  reset();
});

watch(() => props.timeout, (value) => {
  reset();
});

onMounted(async () => {
  init();
});
</script>

<template>
  <Teleport to="body">
    <div class="fullscreen bg-black" :class="props.theme" v-if="idle">
      <Bounce v-if="props.theme == 'bounce'" />
      <WindowsRetro v-if="props.theme == 'windows-retro'" />
    </div>
  </Teleport>
</template>

<style scoped>
.fullscreen {
  display: block;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 900;
  overflow: hidden;
  @apply w-full h-screen;
}

.fullscreen.bounce {
  background: #192958;
}

body.screensaver {
  overflow: hidden;
  background: black;
  padding: 0px;
  margin: 0px;
  width: 100%;
  height: 100% !important;
  max-height: 100% !important;
}
</style>
